import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, LayoutDashboard, ArrowDownToLine, ArrowUpFromLine, ScrollText, Settings2,
  Newspaper, Users as UsersIcon, LogOut, Check, X, RefreshCw, Save, Plus, Trash2, Pencil, Link2,
  Facebook, Twitter, Send, Package, Mail, Coins, MessageCircle,
} from 'lucide-react';
import Logo from '../components/Logo';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const NAV = [
  { key: 'overview', label: 'Overview', Icon: LayoutDashboard },
  { key: 'purchases', label: 'Package Purchases', Icon: Package },
  { key: 'deposits', label: 'Deposits', Icon: ArrowDownToLine },
  { key: 'withdrawals', label: 'Withdrawals', Icon: ArrowUpFromLine },
  { key: 'kyc', label: 'KYC Reviews', Icon: ScrollText },
  { key: 'shares', label: 'Share Transfers', Icon: ArrowUpFromLine },
  { key: 'commissions', label: 'Commissions', Icon: Coins },
  { key: 'dividends', label: 'Dividends', Icon: Coins },
  { key: 'chats', label: 'Live Chats', Icon: MessageCircle },
  { key: 'users', label: 'Users', Icon: UsersIcon },
  { key: 'settings', label: 'USDT & Socials', Icon: Settings2 },
  { key: 'packages', label: 'Packages', Icon: Package },
  { key: 'news', label: 'News Manager', Icon: Newspaper },
];

const AdminPanel = () => {
  const nav = useNavigate();
  const { user, signOut, ready } = useAuth();
  const [view, setView] = useState('overview');

  useEffect(() => {
    if (ready && (!user || user.role !== 'admin')) nav('/admin');
  }, [ready, user, nav]);

  if (!ready || !user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-[#040d08] text-emerald-50 flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-[260px] shrink-0 border-r border-emerald-900/60 bg-[#06170e]">
        <div className="px-5 py-5 border-b border-emerald-900/60">
          <Logo />
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
            <Shield size={11} /> Control Center
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${view === key ? 'bg-emerald-500 text-emerald-950 font-medium' : 'text-emerald-100/80 hover:bg-emerald-900/40'}`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-emerald-900/60">
          <div className="px-2 pb-3 text-[12px] text-emerald-100/60">
            <div className="text-emerald-100 truncate">{user.email}</div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-300">Administrator</div>
          </div>
          <button onClick={() => { signOut(); nav('/admin'); }} className="w-full inline-flex items-center gap-2 rounded-xl border border-emerald-800/60 bg-emerald-950/40 hover:bg-emerald-900/40 text-emerald-100 text-sm px-3 py-2 transition">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed inset-x-0 top-0 z-30 bg-[#06170e] border-b border-emerald-900/60 px-4 py-3 flex items-center justify-between">
        <Logo />
        <select value={view} onChange={(e) => setView(e.target.value)} className="dark-input py-1.5 text-sm w-40">
          {NAV.map((n) => <option key={n.key} value={n.key}>{n.label}</option>)}
        </select>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 p-4 md:p-8 lg:p-10 lg:pt-8 pt-20">
        {view === 'overview' && <Overview />}
        {view === 'purchases' && <Purchases />}
        {view === 'deposits' && <Deposits />}
        {view === 'withdrawals' && <Withdrawals />}
        {view === 'kyc' && <KYCReviews />}
        {view === 'shares' && <ShareTransfersAdmin />}
        {view === 'commissions' && <CommissionsManager />}
        {view === 'dividends' && <DividendsManager />}
        {view === 'chats' && <ChatsManager />}
        {view === 'users' && <UsersList />}
        {view === 'settings' && <SettingsView />}
        {view === 'packages' && <PackagesManager />}
        {view === 'news' && <NewsManager />}
      </main>
    </div>
  );
};

/* ---------- Overview ---------- */
const Overview = () => {
  const [stats, setStats] = useState(null);
  const load = async () => {
    try { const { data } = await API.get('/admin/stats'); setStats(data); } catch {}
  };
  useEffect(() => { load(); }, []);
  const cards = [
    { label: 'Total Users', value: stats?.users ?? '—', color: 'from-emerald-500/20 to-emerald-500/5', ring: 'ring-emerald-500/30' },
    { label: 'Pending Purchases', value: stats?.purchases_pending ?? '—', color: 'from-lime-500/20 to-lime-500/5', ring: 'ring-lime-500/30' },
    { label: 'Pending Deposits', value: stats?.deposits_pending ?? '—', color: 'from-amber-500/20 to-amber-500/5', ring: 'ring-amber-500/30' },
    { label: 'Pending Withdrawals', value: stats?.withdrawals_pending ?? '—', color: 'from-orange-500/20 to-orange-500/5', ring: 'ring-orange-500/30' },
    { label: 'Pending KYC', value: stats?.kyc_pending ?? '—', color: 'from-sky-500/20 to-sky-500/5', ring: 'ring-sky-500/30' },
    { label: 'Published News', value: stats?.news_count ?? '—', color: 'from-cyan-500/20 to-cyan-500/5', ring: 'ring-cyan-500/30' },
  ];
  return (
    <div>
      <Header title="Dashboard Overview" subtitle="Real-time snapshot of the SkyWay Green control panel." onRefresh={load} />
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-2xl border border-emerald-900/60 bg-gradient-to-b ${c.color} p-5 ring-1 ${c.ring}`}>
            <div className="text-[11px] uppercase tracking-widest text-emerald-100/70">{c.label}</div>
            <div className="mt-2 font-serif text-4xl text-white">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 rounded-2xl border border-emerald-900/60 bg-[#06170e] p-6">
        <div className="font-serif text-white text-xl">Welcome, Administrator.</div>
        <p className="mt-2 text-[14px] text-emerald-100/70 leading-relaxed">
          Use the sidebar to manage deposits, withdrawals, KYC reviews, USDT wallet addresses (ERC20 & TRC20), social channels, and news updates.
          Deposits automatically credit user main wallets on approval; withdrawals debit user balance on approval. Every rejection can include a reason that becomes visible to the user.
        </p>
      </div>
    </div>
  );
};

/* ---------- Purchases ---------- */
const Purchases = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const load = async () => {
    try { const { data } = await API.get('/admin/purchases'); setItems(data.items || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    try { await API.post(`/admin/purchases/${id}/approve`); toast({ title: 'Purchase approved', description: 'Shares credited to user.' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection?');
    if (!reason) return;
    try { await API.post(`/admin/purchases/${id}/reject`, { reason }); toast({ title: 'Purchase rejected' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };
  const cancel = async (p) => {
    const confirmText = `Cancel this APPROVED purchase?\n\nUser: ${p.user_email}\nPackage ${p.package_id} · $${p.package_price}\n\nThis will:\n • Remove ${(p.package_shares||0).toLocaleString()} shares from the user\n • Refund any KYC/locked-bonus used\n • Reverse all 7-level referral commissions credited from this purchase\n\nEnter cancellation reason to proceed:`;
    const reason = window.prompt(confirmText);
    if (!reason) return;
    try {
      const { data } = await API.post(`/admin/purchases/${p.id}/cancel`, { reason });
      const r = data.reversal || {};
      toast({
        title: 'Purchase cancelled',
        description: `Shares reversed: ${r.shares_reversed} · Commissions reversed: $${r.commissions_refunded_from_earners} · Locked bonus refunded: $${r.bonus_locked_refunded}`
      });
      load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };
  const filtered = filter === 'ALL' ? items : items.filter((i) => i.status === filter);
  const pendingInst = (p) => (p.installments || []).find((i) => i.status === 'PENDING');
  const approvedCount = (p) => (p.installments || []).filter((i) => i.status === 'APPROVED').length;
  return (
    <div>
      <Header title="Package Purchase Requests" subtitle="Approving credits share units on the first installment. Certificates unlock only after all installments are approved." onRefresh={load} />
      <Filters value={filter} onChange={setFilter} />
      <Table
        cols={['User', 'Package', 'Amount', 'Shares', 'Progress', 'Pending TX', 'Status', 'Actions']}
        rows={filtered.map((p) => {
          const pi = pendingInst(p);
          const done = approvedCount(p);
          const total = p.installment_total || 1;
          return [
            <UserCell email={p.user_email} name={p.user_name} />,
            <div>
              <div className="text-emerald-100 text-sm">Package {p.package_id}</div>
              <div className="text-[11px] text-emerald-100/50">{p.funding_duration}</div>
            </div>,
            <div className="font-serif text-emerald-300 text-lg">${p.package_price.toFixed(2)}</div>,
            <span className="text-emerald-100">{(p.package_shares || 0).toLocaleString()}</span>,
            <div>
              <div className="text-emerald-100 text-sm font-medium">{done}/{total}</div>
              <div className="mt-1 h-1.5 w-24 rounded bg-emerald-950/60 overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${(done / total) * 100}%` }} />
              </div>
            </div>,
            pi ? (
              <div>
                <div className="text-[11px] text-emerald-300 font-semibold">Month {pi.month} · {pi.network}</div>
                <span className="font-mono text-[11px] text-emerald-100/70 truncate max-w-[200px] inline-block align-middle">{pi.tx_hash}</span>
              </div>
            ) : <span className="text-emerald-100/40 text-xs">—</span>,
            <StatusChip status={p.status} reason={p.reason} />,
            pi ? (
              <div className="flex gap-2">
                <button onClick={() => approve(p.id)} data-testid={`admin-approve-purchase-${p.id}`} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs font-medium px-3 py-1.5"><Check size={12} /> Approve</button>
                <button onClick={() => reject(p.id)} data-testid={`admin-reject-purchase-${p.id}`} className="inline-flex items-center gap-1 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-xs font-medium px-3 py-1.5"><X size={12} /> Reject</button>
              </div>
            ) : (p.status === 'APPROVED' || p.status === 'IN_PROGRESS') ? (
              <button
                onClick={() => cancel(p)}
                data-testid={`admin-cancel-purchase-${p.id}`}
                className="inline-flex items-center gap-1 rounded-lg bg-red-600/90 hover:bg-red-600 text-white text-xs font-medium px-3 py-1.5"
                title="Reverse this approved purchase — undo shares, commissions, and locked-bonus debits"
              >
                <X size={12} /> Cancel
              </button>
            ) : <span className="text-emerald-100/40 text-xs">—</span>,
          ];
        })}
      />
    </div>
  );
};

/* ---------- Deposits ---------- */
const Deposits = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('ALL');

  const load = async () => {
    try { const { data } = await API.get('/admin/deposits'); setItems(data.items || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await API.post(`/admin/deposits/${id}/approve`); toast({ title: 'Deposit approved', description: 'User wallet credited.' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection?');
    if (!reason) return;
    try { await API.post(`/admin/deposits/${id}/reject`, { reason }); toast({ title: 'Deposit rejected' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };

  const filtered = filter === 'ALL' ? items : items.filter((i) => i.status === filter);

  return (
    <div>
      <Header title="Deposit Requests" subtitle="Approve to credit user's main wallet. Rejections require a visible reason." onRefresh={load} />
      <Filters value={filter} onChange={setFilter} />
      <Table
        cols={['User', 'Amount', 'Network', 'TX Hash', 'Status', 'Actions']}
        rows={filtered.map((d) => [
          <UserCell email={d.user_email} name={d.user_name} />,
          <div className="font-serif text-emerald-300 text-lg">${d.amount.toFixed(2)}</div>,
          <span className="text-emerald-100">{d.network}</span>,
          <span className="font-mono text-[12px] text-emerald-100/70 truncate max-w-[220px] inline-block align-middle">{d.tx_hash}</span>,
          <StatusChip status={d.status} reason={d.reason} />,
          d.status === 'PENDING' ? (
            <div className="flex gap-2">
              <button onClick={() => approve(d.id)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs font-medium px-3 py-1.5"><Check size={12} /> Approve</button>
              <button onClick={() => reject(d.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-xs font-medium px-3 py-1.5"><X size={12} /> Reject</button>
            </div>
          ) : <span className="text-emerald-100/40 text-xs">—</span>,
        ])}
      />
    </div>
  );
};

/* ---------- Withdrawals ---------- */
const Withdrawals = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const load = async () => {
    try { const { data } = await API.get('/admin/withdrawals'); setItems(data.items || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    try { await API.post(`/admin/withdrawals/${id}/approve`); toast({ title: 'Withdrawal approved' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection?');
    if (!reason) return;
    try { await API.post(`/admin/withdrawals/${id}/reject`, { reason }); toast({ title: 'Withdrawal rejected' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };
  const filtered = filter === 'ALL' ? items : items.filter((i) => i.status === filter);
  return (
    <div>
      <Header title="Withdrawal Requests" subtitle="Approving debits user balance; rejecting keeps funds untouched." onRefresh={load} />
      <Filters value={filter} onChange={setFilter} />
      <Table
        cols={['User', 'Source', 'Amount', 'Network', 'Destination', 'Status', 'Actions']}
        rows={filtered.map((w) => [
          <UserCell email={w.user_email} name={w.user_name} />,
          <span className="text-emerald-100 uppercase text-xs">{w.source}</span>,
          <div className="font-serif text-emerald-300 text-lg">${w.amount.toFixed(2)}</div>,
          <span className="text-emerald-100">{w.network}</span>,
          <span className="font-mono text-[12px] text-emerald-100/70 truncate max-w-[200px] inline-block align-middle">{w.destination}</span>,
          <StatusChip status={w.status} reason={w.reason} />,
          w.status === 'PENDING' ? (
            <div className="flex gap-2">
              <button onClick={() => approve(w.id)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs font-medium px-3 py-1.5"><Check size={12} /> Approve</button>
              <button onClick={() => reject(w.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-xs font-medium px-3 py-1.5"><X size={12} /> Reject</button>
            </div>
          ) : <span className="text-emerald-100/40 text-xs">—</span>,
        ])}
      />
    </div>
  );
};

/* ---------- KYC ---------- */
const KYCReviews = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const load = async () => {
    try { const { data } = await API.get('/admin/kyc'); setItems(data.items || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  const approve = async (uid) => {
    try { await API.post(`/admin/kyc/${uid}/approve`); toast({ title: 'KYC approved' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };
  const reject = async (uid) => {
    const reason = window.prompt('Reason for KYC rejection (will be shown to user)?');
    if (!reason) return;
    try { await API.post(`/admin/kyc/${uid}/reject`, { reason }); toast({ title: 'KYC rejected' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };
  const startEdit = (k) => {
    setEditingId(k.user_id);
    setEditForm({
      firstName: k.firstName || '', lastName: k.lastName || '',
      dob: k.dob || '', docNo: k.docNo || '', mobile: k.mobile || '',
      addr1: k.addr1 || '', addr2: k.addr2 || '',
      state: k.state || '', zip: k.zip || '', country: k.country || '',
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const saveEdit = async (uid) => {
    try {
      await API.patch(`/admin/kyc/${uid}`, editForm);
      toast({ title: 'KYC details updated' });
      cancelEdit();
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Update failed' });
    }
  };
  const filtered = filter === 'ALL' ? items : items.filter((i) => i.status === filter);
  return (
    <div>
      <Header title="KYC Verification Queue" subtitle="Review user identity submissions and approve or reject with a reason." onRefresh={load} />
      <Filters value={filter} onChange={setFilter} />
      <div className="mt-4 space-y-3">
        {filtered.length === 0 && <div className="rounded-2xl border border-emerald-900/60 bg-[#06170e] p-6 text-emerald-100/60 text-sm">No KYC entries.</div>}
        {filtered.map((k) => (
          <div key={k.user_id} className="rounded-2xl border border-emerald-900/60 bg-[#06170e] p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-serif text-white text-lg" data-testid={`kyc-name-${k.user_id}`}>{k.firstName} {k.lastName}</div>
                <div className="text-[12px] text-emerald-100/60">{k.user_email} · Doc: <span className="text-emerald-300 font-mono" data-testid={`kyc-docno-${k.user_id}`}>{k.docNo}</span></div>
              </div>
              <StatusChip status={k.status} reason={k.reason} />
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-[13px]">
              <Detail label="DOB" value={k.dob} />
              <Detail label="Mobile" value={k.mobile} />
              <Detail label="Country" value={k.country} />
              <Detail label="ZIP" value={k.zip} />
              <Detail label="State" value={k.state} />
              <Detail label="Address 1" value={k.addr1} span="md:col-span-2" />
              <Detail label="Address 2" value={k.addr2 || '—'} />
            </div>

            {/* Document Images — for name & docNo verification */}
            {(k.doc_front || k.doc_back || k.selfie) && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-100/50 mb-2">Submitted Documents (click to enlarge)</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <KYCDocPreview label="Document Front" src={k.doc_front} />
                  <KYCDocPreview label="Document Back" src={k.doc_back} />
                  <KYCDocPreview label="Selfie with ID" src={k.selfie} />
                </div>
              </div>
            )}

            {/* AI Verification Verdict — shown when submission was auto-checked */}
            {k.ai_verdict && (
              <div className="mt-4 rounded-xl border border-cyan-500/30 bg-cyan-950/25 p-4" data-testid={`ai-verdict-${k.user_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold">AI Verification ({k.ai_verdict.model || 'auto'})</div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
                      k.ai_verdict.verdict === 'APPROVE' ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300' :
                      k.ai_verdict.verdict === 'REJECT'  ? 'border-red-500/50 bg-red-500/15 text-red-300' :
                                                          'border-amber-500/50 bg-amber-500/15 text-amber-300'
                    }`}>
                      {k.ai_verdict.verdict || 'PENDING'}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-500/40 bg-cyan-500/10 px-2.5 py-0.5 text-[11px] text-cyan-200 font-mono">
                      Confidence: {k.ai_verdict.confidence ?? '—'}%
                    </span>
                  </div>
                </div>
                {k.ai_verdict.extracted && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-[12px]">
                    <AIField label="Doc Name" value={k.ai_verdict.extracted.full_name} matched={k.ai_verdict.matches?.name_match} />
                    <AIField label="Doc DOB" value={k.ai_verdict.extracted.date_of_birth} matched={k.ai_verdict.matches?.dob_match} />
                    <AIField label="Doc Number" value={k.ai_verdict.extracted.document_number} matched={k.ai_verdict.matches?.doc_number_match} />
                  </div>
                )}
                {Array.isArray(k.ai_verdict.mismatches) && k.ai_verdict.mismatches.length > 0 && (
                  <div className="mt-3 rounded-lg bg-red-950/30 border border-red-500/30 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-widest text-red-300 mb-1">Mismatches</div>
                    <ul className="text-[12px] text-red-100/85 list-disc pl-4 space-y-0.5">
                      {k.ai_verdict.mismatches.map((m, idx) => <li key={idx}>{m}</li>)}
                    </ul>
                  </div>
                )}
                {k.ai_verdict.reasoning && (
                  <div className="mt-2 text-[11px] text-cyan-100/70 italic">{k.ai_verdict.reasoning}</div>
                )}
                {k.ai_verdict.error && (
                  <div className="mt-2 text-[11px] text-amber-300">AI Error: {k.ai_verdict.error}</div>
                )}
              </div>
            )}

            {k.status === 'PENDING' && editingId !== k.user_id && (
              <div className="mt-4 flex gap-2 flex-wrap">
                <button data-testid={`kyc-approve-${k.user_id}`} onClick={() => approve(k.user_id)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs font-medium px-3 py-1.5"><Check size={12} /> Approve</button>
                <button data-testid={`kyc-reject-${k.user_id}`} onClick={() => reject(k.user_id)} className="inline-flex items-center gap-1 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-xs font-medium px-3 py-1.5"><X size={12} /> Reject with reason</button>
                <button data-testid={`kyc-edit-${k.user_id}`} onClick={() => startEdit(k)} className="inline-flex items-center gap-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-emerald-950 text-xs font-medium px-3 py-1.5">Edit Details</button>
              </div>
            )}
            {k.status !== 'PENDING' && editingId !== k.user_id && (
              <div className="mt-4 flex gap-2">
                <button data-testid={`kyc-edit-${k.user_id}`} onClick={() => startEdit(k)} className="inline-flex items-center gap-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-emerald-950 text-xs font-medium px-3 py-1.5">Edit Details</button>
              </div>
            )}
            {editingId === k.user_id && (
              <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-950/25 p-4" data-testid={`kyc-edit-form-${k.user_id}`}>
                <div className="text-[11px] uppercase tracking-widest text-amber-200 font-semibold mb-3">Editing KYC personal details</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-[12px]">
                  {[
                    ['firstName', 'First Name'],
                    ['lastName', 'Last Name'],
                    ['dob', 'Date of Birth (YYYY-MM-DD)'],
                    ['docNo', 'Document No.'],
                    ['mobile', 'Mobile'],
                    ['country', 'Country'],
                    ['state', 'State'],
                    ['zip', 'ZIP'],
                    ['addr1', 'Address Line 1'],
                    ['addr2', 'Address Line 2'],
                  ].map(([key, lbl]) => (
                    <label key={key} className="flex flex-col gap-1">
                      <span className="text-[10px] uppercase tracking-widest text-emerald-100/60">{lbl}</span>
                      <input
                        data-testid={`kyc-edit-input-${key}`}
                        value={editForm[key] || ''}
                        onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                        className="rounded-lg bg-[#06170e] border border-emerald-800/60 text-white px-3 py-1.5 focus:outline-none focus:border-emerald-500"
                      />
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <button data-testid={`kyc-edit-save-${k.user_id}`} onClick={() => saveEdit(k.user_id)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs font-semibold px-3 py-1.5">Save</button>
                  <button onClick={cancelEdit} className="inline-flex items-center gap-1 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-3 py-1.5">Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Detail = ({ label, value, span }) => (
  <div className={`rounded-lg bg-emerald-950/40 border border-emerald-900/60 px-3 py-2 ${span || ''}`}>
    <div className="text-[10px] uppercase tracking-widest text-emerald-100/50">{label}</div>
    <div className="text-emerald-100">{value || '—'}</div>
  </div>
);

const AIField = ({ label, value, matched }) => (
  <div className={`rounded-lg border px-3 py-2 ${matched === true ? 'border-emerald-500/40 bg-emerald-500/8' : matched === false ? 'border-red-500/40 bg-red-500/8' : 'border-cyan-900/60 bg-cyan-950/30'}`}>
    <div className="flex items-center justify-between">
      <div className="text-[10px] uppercase tracking-widest text-emerald-100/50">{label}</div>
      {matched === true && <Check size={10} className="text-emerald-400" />}
      {matched === false && <X size={10} className="text-red-400" />}
    </div>
    <div className="text-emerald-100 text-[12px] font-mono truncate">{value || '—'}</div>
  </div>
);

const KYCDocPreview = ({ label, src }) => {
  const [open, setOpen] = useState(false);
  if (!src) {
    return (
      <div className="rounded-lg border border-emerald-900/60 bg-emerald-950/40 aspect-[4/3] flex items-center justify-center text-[11px] text-emerald-100/40">
        {label}<br /><span className="text-[10px]">Not provided</span>
      </div>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group rounded-lg overflow-hidden border border-emerald-900/60 bg-black/40 aspect-[4/3] relative"
      >
        <img src={src} alt={label} className="absolute inset-0 h-full w-full object-cover transition group-hover:scale-105" />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-2 py-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-200 text-left">{label}</div>
        </div>
      </button>
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[1300] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
        >
          <img src={src} alt={label} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
          <div className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 p-2 text-white">
            <X size={20} />
          </div>
          <div className="absolute bottom-4 left-4 text-white text-sm font-semibold uppercase tracking-widest">{label}</div>
        </div>
      )}
    </>
  );
};


/* ---------- Users ---------- */
const UsersList = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const load = async () => {
    try { const { data } = await API.get('/admin/users'); setItems(data.items || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  const del = async (u) => {
    const confirmed = window.confirm(`Delete user ${u.email} PERMANENTLY?\n\nThis will remove:\n• The account\n• All purchases, deposits, withdrawals\n• All commissions, transfers, share transfers\n• All KYC and history\n\nThis action CANNOT be undone. Continue?`);
    if (!confirmed) return;
    try {
      await API.delete(`/admin/users/${u.id}`);
      toast({ title: 'User deleted', description: `${u.email} and all related records removed.` });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Could not delete user.' });
    }
  };
  return (
    <div>
      <Header title="Users" subtitle="All registered co-owners." onRefresh={load} />
      <Table
        cols={['User', 'ID', 'Country', 'Main Wallet', 'Bonus', 'Shares', 'KYC', '']}
        rows={items.map((u) => [
          <UserCell email={u.email} name={u.displayName} />,
          <span className="text-emerald-100 font-mono text-xs">{u.memberId}</span>,
          <span className="text-emerald-100">{u.country || '—'}</span>,
          <span className="font-serif text-emerald-300">${(u.main_wallet || 0).toFixed(2)}</span>,
          <span className="font-serif text-amber-400">${(u.bonus_wallet || 0).toFixed(2)}</span>,
          <span className="text-emerald-100">{u.total_shares || 0}</span>,
          <StatusChip status={u.kyc_status} />,
          <button
            data-testid={`delete-user-${u.id}`}
            onClick={() => del(u)}
            className="inline-flex items-center gap-1 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-xs font-medium px-3 py-1.5"
          >
            <Trash2 size={12} /> Delete
          </button>,
        ])}
      />
    </div>
  );
};

/* ---------- Share Transfers (SkyWay → ApexForge.io) ---------- */
const ShareTransfersAdmin = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const load = async () => {
    try { const { data } = await API.get('/admin/share-transfers'); setItems(data.items || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    try {
      await API.post(`/admin/share-transfers/${id}/approve`);
      toast({ title: 'Transfer approved', description: 'User will see status as Successfully Transferred.' });
      load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection (shown to user)?');
    if (!reason) return;
    try {
      await API.post(`/admin/share-transfers/${id}/reject`, { reason });
      toast({ title: 'Transfer rejected' });
      load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };
  return (
    <div>
      <Header title="Share Transfers" subtitle="Move SkyWay Green shares to ApexForge.io. Approve after confirming the target account KYC matches." onRefresh={load} />
      <Table
        cols={['User', 'Shares', 'ApexForge Email', 'Requested', 'Status', 'Actions']}
        rows={items.map((t) => [
          <div>
            <div className="text-emerald-50 font-medium text-[13px]">{t.user_name || t.user_email}</div>
            <div className="text-[11px] text-emerald-100/60 truncate max-w-[220px]">{t.user_email}</div>
          </div>,
          <span className="font-serif text-emerald-300 text-[15px]">{t.shares_qty}</span>,
          <span className="text-emerald-100 text-[12px] break-all">{t.apex_email || '—'}</span>,
          <span className="text-emerald-100/70 text-[11.5px]">{new Date(t.created_at).toLocaleString()}</span>,
          <StatusChip status={t.status} />,
          t.status === 'PENDING' ? (
            <div className="flex gap-2 flex-wrap">
              <button data-testid={`share-approve-${t.id}`} onClick={() => approve(t.id)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs font-medium px-3 py-1.5"><Check size={12} /> Approve</button>
              <button data-testid={`share-reject-${t.id}`} onClick={() => reject(t.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-xs font-medium px-3 py-1.5"><X size={12} /> Reject</button>
            </div>
          ) : <span className="text-emerald-100/50 text-[11px]">—</span>,
        ])}
      />
    </div>
  );
};

/* ---------- Commissions ---------- */
const CommissionsManager = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await API.get('/admin/commissions');
      setItems(data.items || []);
    } catch (e) {
      // Fallback: fetch raw ledger
      try {
        const { data } = await API.get('/admin/commissions/ledger');
        setItems(data.items || []);
      } catch {}
    }
  };
  useEffect(() => { load(); }, []);
  const rebuild = async () => {
    if (!window.confirm('Recalculate all referral commissions from the ground up?\n\nThis wipes existing commission records and rebuilds them from every approved installment. Use this if some users are missing commissions.')) return;
    setBusy(true);
    try {
      const { data } = await API.post('/admin/commissions/backfill?rebuild=true');
      toast({ title: 'Commissions rebuilt', description: `${data.commissions_created || 0} records created, $${data.total_credited || 0} distributed.` });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Please try again.' });
    } finally { setBusy(false); }
  };
  const backfillOnly = async () => {
    if (!window.confirm('Add missing commissions without touching existing ones?')) return;
    setBusy(true);
    try {
      const { data } = await API.post('/admin/commissions/backfill');
      toast({ title: 'Backfill complete', description: `${data.commissions_created || 0} new records created, $${data.total_credited || 0} credited.` });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Please try again.' });
    } finally { setBusy(false); }
  };
  const total = items.reduce((a, i) => a + (i.amount || 0), 0);
  return (
    <div>
      <Header title="Commissions" subtitle="7-level referral commissions. Use Rebuild if any user is missing payouts." onRefresh={load} />
      <div className="mb-4 flex gap-2 flex-wrap">
        <button
          onClick={backfillOnly}
          disabled={busy}
          data-testid="commission-backfill-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-emerald-950 font-semibold px-4 py-2"
        >
          {busy ? 'Working…' : 'Backfill Missing'}
        </button>
        <button
          onClick={rebuild}
          disabled={busy}
          data-testid="commission-rebuild-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-red-500/90 hover:bg-red-500 disabled:opacity-50 text-white font-semibold px-4 py-2"
        >
          {busy ? 'Working…' : 'Rebuild All (destructive)'}
        </button>
        <div className="ml-auto text-emerald-100/70 text-[12.5px]">
          Total records: <b className="text-emerald-200">{items.length}</b> · Total credited: <b className="text-emerald-300">${total.toFixed(2)}</b>
        </div>
      </div>
      <Table
        cols={['Earner', 'From', 'Level', 'Rate %', 'Amount', 'Purchase', 'Month', 'When']}
        rows={items.slice(0, 400).map((c) => [
          <div>
            <div className="text-emerald-50 text-[13px]">{c.user_email}</div>
          </div>,
          <div>
            <div className="text-emerald-100/80 text-[12px]">{c.from_user_email || c.from_user_id}</div>
          </div>,
          <span className="text-emerald-100 font-serif">L{c.level}</span>,
          <span className="text-emerald-100">{c.rate_pct}%</span>,
          <span className="font-serif text-amber-400">${(c.amount || 0).toFixed(2)}</span>,
          <span className="text-emerald-100/60 text-[11px] font-mono">{(c.purchase_id || '').slice(0, 8)}…</span>,
          <span className="text-emerald-100/80">{c.installment_month}</span>,
          <span className="text-emerald-100/60 text-[11px]">{c.created_at ? new Date(c.created_at).toLocaleString() : '—'}</span>,
        ])}
      />
    </div>
  );
};


/* ---------- Package Manager ---------- */
const PackagesManager = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const load = async () => {
    try {
      const { data } = await API.get('/packages');
      setItems(data.items || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);
  const update = (idx, field, val) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [field]: field === 'id' || field === 'shares' ? parseInt(val || 0, 10) : parseFloat(val || 0) };
    setItems(copy);
  };
  const saveAll = async () => {
    // Basic sanity check — no empty rows
    for (const p of items) {
      if (!p.id || !p.price || !p.shares || !p.monthly) {
        return toast({ title: 'Invalid data', description: `Package ${p.id || '?'} has empty fields.` });
      }
    }
    setSavingId('all');
    try {
      await API.put('/admin/packages', { packages: items });
      toast({ title: 'Packages saved', description: 'Live for all users immediately.' });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Please try again.' });
    } finally {
      setSavingId(null);
    }
  };
  const resetDefaults = async () => {
    if (!window.confirm('Reset all packages to factory defaults?')) return;
    // Send defaults matching backend DEFAULT_PACKAGES
    const defaults = [
      { id: 1, price: 50, shares: 7000, monthly: 5 },
      { id: 2, price: 100, shares: 15000, monthly: 10 },
      { id: 3, price: 250, shares: 39000, monthly: 25 },
      { id: 4, price: 500, shares: 82000, monthly: 50 },
      { id: 5, price: 1000, shares: 180000, monthly: 100 },
      { id: 10, price: 1500, shares: 280000, monthly: 150 },
      { id: 6, price: 2000, shares: 380000, monthly: 200 },
      { id: 7, price: 5000, shares: 1050000, monthly: 500 },
      { id: 8, price: 10000, shares: 2500000, monthly: 1000 },
    ];
    try {
      await API.put('/admin/packages', { packages: defaults });
      toast({ title: 'Reset to defaults' });
      load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };

  return (
    <div>
      <Header title="Packages" subtitle="Edit each package's price, share quantity and monthly installment. Changes go live for every user instantly." onRefresh={load} />
      <div className="mt-4 rounded-2xl border border-emerald-900/60 bg-[#06170e] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-emerald-950/50 border-b border-emerald-900/60">
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-emerald-300/80">Package ID</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-emerald-300/80">Price (USD)</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-emerald-300/80">Share Units</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-emerald-300/80">Monthly (USD)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p, idx) => (
                <tr key={idx} className="border-b border-emerald-900/40" data-testid={`pkg-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={p.id}
                      onChange={(e) => update(idx, 'id', e.target.value)}
                      data-testid={`pkg-${idx}-id`}
                      className="w-20 rounded-lg bg-[#0a2416] border border-emerald-800/60 text-white px-3 py-1.5 focus:outline-none focus:border-emerald-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      step="0.01"
                      value={p.price}
                      onChange={(e) => update(idx, 'price', e.target.value)}
                      data-testid={`pkg-${idx}-price`}
                      className="w-32 rounded-lg bg-[#0a2416] border border-emerald-800/60 text-white px-3 py-1.5 focus:outline-none focus:border-emerald-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={p.shares}
                      onChange={(e) => update(idx, 'shares', e.target.value)}
                      data-testid={`pkg-${idx}-shares`}
                      className="w-40 rounded-lg bg-[#0a2416] border border-emerald-800/60 text-white px-3 py-1.5 focus:outline-none focus:border-emerald-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      step="0.01"
                      value={p.monthly}
                      onChange={(e) => update(idx, 'monthly', e.target.value)}
                      data-testid={`pkg-${idx}-monthly`}
                      className="w-32 rounded-lg bg-[#0a2416] border border-emerald-800/60 text-white px-3 py-1.5 focus:outline-none focus:border-emerald-500"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button
          onClick={saveAll}
          disabled={savingId === 'all'}
          data-testid="save-packages-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-emerald-950 font-semibold px-5 py-2.5"
        >
          <Save size={14} /> {savingId === 'all' ? 'Saving…' : 'Save All Packages'}
        </button>
        <button
          onClick={resetDefaults}
          className="inline-flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-medium px-4 py-2.5"
        >
          Reset to Defaults
        </button>
      </div>
      <p className="mt-3 text-[12px] text-emerald-100/60">
        Existing purchases keep the shares they were bought with. Changes apply to <b>new</b> purchases only.
      </p>
    </div>
  );
};


/* ---------- Settings (USDT + Socials) ---------- */
const SettingsView = () => {
  const { toast } = useToast();
  const [s, setS] = useState({ usdt_erc20_address: '', usdt_erc20_qr: '', usdt_trc20_address: '', usdt_trc20_qr: '', usdt_bep20_address: '', usdt_bep20_qr: '', contact_email: '', socials: {} });
  const load = async () => {
    try { const { data } = await API.get('/settings'); setS({ ...data, socials: data.socials || {} }); } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await API.put('/admin/settings', s);
      toast({ title: 'Settings saved', description: 'Live for all users immediately.' });
      load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };

  const setSocial = (key, val) => setS({ ...s, socials: { ...s.socials, [key]: val } });

  const readFile = (file, cb) => {
    const reader = new FileReader();
    reader.onload = () => cb(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <Header title="USDT & Social Channels" subtitle="Update deposit wallet addresses, QR images, contact email, and public social links." onRefresh={load} />

      {/* Contact Email */}
      <div className="mt-6 rounded-2xl border border-emerald-900/60 bg-[#06170e] p-6">
        <div className="flex items-center gap-2 text-emerald-300">
          <Mail size={16} /> <span className="font-medium">Public Contact Email</span>
        </div>
        <p className="mt-1 text-[12px] text-emerald-100/60">This email is shown on the footer under the company address and used as the primary contact channel.</p>
        <div className="mt-4">
          <label className="block">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300 mb-2">Email Address</span>
            <input
              type="email"
              value={s.contact_email || ''}
              onChange={(e) => setS({ ...s, contact_email: e.target.value })}
              placeholder="contact@skywaygreen.com"
              className="dark-input"
              data-testid="admin-contact-email-input"
            />
          </label>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <NetworkCard title="USDT ERC20 (Ethereum)" address={s.usdt_erc20_address} qr={s.usdt_erc20_qr}
          onAddress={(v) => setS({ ...s, usdt_erc20_address: v })}
          onQr={(f) => readFile(f, (b64) => setS({ ...s, usdt_erc20_qr: b64 }))}
          onClearQr={() => setS({ ...s, usdt_erc20_qr: '' })}
        />
        <NetworkCard title="USDT BEP20 (BNB Smart Chain)" address={s.usdt_bep20_address} qr={s.usdt_bep20_qr}
          onAddress={(v) => setS({ ...s, usdt_bep20_address: v })}
          onQr={(f) => readFile(f, (b64) => setS({ ...s, usdt_bep20_qr: b64 }))}
          onClearQr={() => setS({ ...s, usdt_bep20_qr: '' })}
        />
        <NetworkCard title="USDT TRC20 (Tron)" address={s.usdt_trc20_address} qr={s.usdt_trc20_qr}
          onAddress={(v) => setS({ ...s, usdt_trc20_address: v })}
          onQr={(f) => readFile(f, (b64) => setS({ ...s, usdt_trc20_qr: b64 }))}
          onClearQr={() => setS({ ...s, usdt_trc20_qr: '' })}
        />
      </div>

      <div className="mt-6 rounded-2xl border border-emerald-900/60 bg-[#06170e] p-6">
        <div className="flex items-center gap-2 text-emerald-300">
          <Link2 size={16} /> <span className="font-medium">Social Media Channels</span>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { key: 'facebook', Icon: Facebook, label: 'Facebook' },
            { key: 'twitter', Icon: Twitter, label: 'Twitter / X' },
            { key: 'telegram', Icon: Send, label: 'Telegram' },
          ].map(({ key, Icon, label }) => (
            <label key={key} className="block">
              <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300 mb-2">
                <Icon size={12} /> {label}
              </span>
              <input value={s.socials[key] || ''} onChange={(e) => setSocial(key, e.target.value)} placeholder={`https://${key}.com/...`} className="dark-input" />
            </label>
          ))}
        </div>
      </div>

      <button onClick={save} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold px-6 py-3 transition">
        <Save size={16} /> Save All Settings
      </button>

      <AdminChangePassword />
    </div>
  );
};

const AdminChangePassword = () => {
  const [curPwd, setCurPwd] = React.useState('');
  const [newPwd, setNewPwd] = React.useState('');
  const [confirmPwd, setConfirmPwd] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) return toast({ title: 'Password too short', description: 'Minimum 8 characters.' });
    if (newPwd !== confirmPwd) return toast({ title: 'Passwords do not match' });
    if (newPwd === curPwd) return toast({ title: 'Choose a different password' });
    setBusy(true);
    try {
      await API.post('/admin/change-password', { current_password: curPwd, new_password: newPwd });
      toast({ title: 'Password updated', description: 'Use your new password on next login.' });
      setCurPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err) {
      toast({ title: 'Change failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-10 rounded-2xl border border-red-500/40 bg-red-950/20 p-6" data-testid="admin-change-password-card">
      <div className="flex items-center gap-2 mb-1">
        <Shield size={16} className="text-red-400" />
        <div className="font-serif text-lg text-red-200">Change Admin Password</div>
      </div>
      <div className="text-[12px] text-red-200/70 mb-4">Do this immediately if you've been using the default password. Minimum 8 characters.</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input
          data-testid="admin-current-password"
          type="password"
          placeholder="Current password"
          value={curPwd}
          onChange={(e) => setCurPwd(e.target.value)}
          required
          className="dark-input"
        />
        <input
          data-testid="admin-new-password"
          type="password"
          placeholder="New password (min 8)"
          value={newPwd}
          onChange={(e) => setNewPwd(e.target.value)}
          required minLength={8}
          className="dark-input"
        />
        <input
          data-testid="admin-confirm-password"
          type="password"
          placeholder="Confirm new password"
          value={confirmPwd}
          onChange={(e) => setConfirmPwd(e.target.value)}
          required minLength={8}
          className="dark-input"
        />
      </div>
      <button
        type="submit"
        disabled={busy || !curPwd || !newPwd || !confirmPwd}
        data-testid="admin-change-password-btn"
        className="mt-4 inline-flex items-center gap-2 rounded-xl bg-red-500 hover:bg-red-400 disabled:opacity-60 text-red-950 font-semibold px-5 py-2.5 transition"
      >
        {busy ? 'Updating…' : 'Update Password'}
      </button>
    </form>
  );
};

const NetworkCard = ({ title, address, qr, onAddress, onQr, onClearQr }) => (
  <div className="rounded-2xl border border-emerald-900/60 bg-[#06170e] p-6">
    <div className="font-medium text-white">{title}</div>
    <div className="mt-4 space-y-4">
      <label className="block">
        <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300 mb-2">Wallet Address</span>
        <input value={address || ''} onChange={(e) => onAddress(e.target.value)} placeholder="Enter deposit wallet address" className="dark-input font-mono text-[13px]" />
      </label>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300 mb-2">QR Code Image</div>
        <div className="flex items-start gap-3 flex-wrap">
          <div className="h-32 w-32 rounded-lg border border-emerald-900/60 bg-white/5 flex items-center justify-center overflow-hidden">
            {qr ? <img src={qr} alt="QR" className="h-full w-full object-contain bg-white p-1" /> : <span className="text-[11px] text-emerald-100/40 text-center px-2">No QR uploaded</span>}
          </div>
          <div className="flex flex-col gap-2">
            <label className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-800/60 bg-emerald-950/40 hover:bg-emerald-900/40 text-emerald-100 text-xs px-3 py-2 cursor-pointer">
              Upload PNG/JPG
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onQr(e.target.files[0])} />
            </label>
            {qr && <button onClick={onClearQr} className="text-red-400 hover:text-red-300 text-xs">Remove</button>}
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ---------- News Manager ---------- */
const emptyNews = { category: 'Announcement', date: '', title: '', excerpt: '', image: '', featured: false, content: '', notify_users: true };
const NewsManager = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // id being edited or null
  const [form, setForm] = useState(emptyNews);

  const load = async () => {
    try { const { data } = await API.get('/news'); setItems(data.items || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await API.put(`/admin/news/${editing}`, form);
        toast({ title: 'News updated' });
      } else {
        const { data } = await API.post('/admin/news', form);
        toast({
          title: 'News published',
          description: data?.broadcast_scheduled ? 'Emailing all registered users…' : 'Users will not be emailed.',
        });
      }
      setForm(emptyNews); setEditing(null); load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };
  const startEdit = (n) => {
    setEditing(n.id);
    setForm({ category: n.category, date: n.date, title: n.title, excerpt: n.excerpt, image: n.image, featured: !!n.featured, content: n.content || '', notify_users: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const del = async (id) => {
    if (!window.confirm('Delete this news item?')) return;
    try { await API.delete(`/admin/news/${id}`); toast({ title: 'Deleted' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
  };

  return (
    <div>
      <Header title="News Manager" subtitle="Publish, edit or remove ecosystem news. Users see updates instantly on the site." onRefresh={load} />

      <form onSubmit={submit} className="mt-6 rounded-2xl border border-emerald-900/60 bg-[#06170e] p-6 space-y-4">
        <div className="flex items-center gap-2 text-emerald-300">
          {editing ? <Pencil size={16} /> : <Plus size={16} />}
          <span className="font-medium">{editing ? 'Edit News Item' : 'Create News Item'}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DarkField label="Category">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="dark-input appearance-none">
              {['Announcement', 'Partnership', 'Technology', 'Community', 'Engineering'].map((c) => <option key={c} className="bg-[#04160d]">{c}</option>)}
            </select>
          </DarkField>
          <DarkField label="Date">
            <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} placeholder="Jul 12, 2025" className="dark-input" required />
          </DarkField>
          <DarkField label="Featured">
            <label className="flex items-center gap-2 text-emerald-100 text-sm mt-3">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-emerald-500" />
              Show as featured story
            </label>
          </DarkField>
        </div>
        <DarkField label="Title">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="dark-input" />
        </DarkField>
        <DarkField label="Excerpt">
          <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} required rows={3} className="dark-input" />
        </DarkField>
        <DarkField label="Full Article Content">
          <textarea
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            rows={10}
            placeholder={'Write the full news body here. Use blank lines to separate paragraphs. Basic HTML (<b>, <i>, <a>, <ul>, <li>) is supported.'}
            className="dark-input"
          />
          <div className="mt-1 text-[11px] text-emerald-300/60">Shown when users click “Read article”. Leave blank to reuse the excerpt.</div>
        </DarkField>
        <DarkField label="Image URL">
          <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} required placeholder="https://..." className="dark-input" />
        </DarkField>
        {!editing && (
          <label className="flex items-center gap-2 text-emerald-100 text-sm">
            <input
              type="checkbox"
              checked={form.notify_users}
              onChange={(e) => setForm({ ...form, notify_users: e.target.checked })}
              className="h-4 w-4 accent-emerald-500"
              data-testid="news-notify-users"
            />
            Email this article to all registered users
          </label>
        )}
        <div className="flex gap-2">
          <button type="submit" className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold px-5 py-2.5"><Save size={14} /> {editing ? 'Update' : 'Publish'}</button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(emptyNews); }} className="rounded-xl border border-emerald-800/60 bg-emerald-950/40 hover:bg-emerald-900/40 text-emerald-100 text-sm px-4 py-2.5">Cancel</button>}
        </div>
      </form>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((n) => (
          <article key={n.id} className="rounded-2xl border border-emerald-900/60 bg-[#06170e] overflow-hidden">
            <div className="aspect-[16/8] overflow-hidden bg-emerald-950">
              <img src={n.image} alt={n.title} className="h-full w-full object-cover" />
            </div>
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-widest text-emerald-400">{n.category} · {n.date}{n.featured && <span className="ml-2 text-amber-400">★ Featured</span>}</div>
              <div className="mt-1 font-serif text-white text-lg leading-snug">{n.title}</div>
              <div className="mt-1 text-[13px] text-emerald-100/60 line-clamp-2">{n.excerpt}</div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => startEdit(n)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-900/50 hover:bg-emerald-900/70 text-emerald-100 text-xs px-3 py-1.5"><Pencil size={12} /> Edit</button>
                <button onClick={() => del(n.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-xs px-3 py-1.5"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

const DarkField = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300 mb-2">{label}</span>
    {children}
  </label>
);

/* ---------- Shared ---------- */
const Header = ({ title, subtitle, onRefresh }) => (
  <div className="flex items-start justify-between gap-4 flex-wrap">
    <div>
      <h1 className="font-serif text-white text-3xl md:text-4xl tracking-tight">{title}</h1>
      <p className="mt-1 text-emerald-100/60 text-sm">{subtitle}</p>
    </div>
    {onRefresh && (
      <button onClick={onRefresh} className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-800/60 bg-emerald-950/40 hover:bg-emerald-900/40 text-emerald-100 text-sm px-3 py-2 transition">
        <RefreshCw size={14} /> Refresh
      </button>
    )}
  </div>
);

const Filters = ({ value, onChange }) => (
  <div className="mt-5 flex flex-wrap gap-2">
    {['ALL', 'PENDING', 'APPROVED', 'REJECTED'].map((s) => (
      <button key={s} onClick={() => onChange(s)} className={`rounded-full px-3.5 py-1.5 text-[12px] font-medium uppercase tracking-widest transition ${value === s ? 'bg-emerald-500 text-emerald-950' : 'bg-emerald-950/40 border border-emerald-800/60 text-emerald-100 hover:bg-emerald-900/40'}`}>{s}</button>
    ))}
  </div>
);

const UserCell = ({ email, name }) => (
  <div className="min-w-0">
    <div className="text-emerald-100 text-sm truncate max-w-[220px]">{name || email}</div>
    <div className="text-emerald-100/50 text-[12px] truncate max-w-[220px]">{email}</div>
  </div>
);

const StatusChip = ({ status, reason }) => {
  const map = {
    PENDING: { c: 'text-amber-300 border-amber-500/40 bg-amber-500/10', d: 'bg-amber-400' },
    IN_PROGRESS: { c: 'text-sky-300 border-sky-500/40 bg-sky-500/10', d: 'bg-sky-400' },
    APPROVED: { c: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10', d: 'bg-emerald-400' },
    REJECTED: { c: 'text-red-300 border-red-500/40 bg-red-500/10', d: 'bg-red-400' },
    NOT_SUBMITTED: { c: 'text-emerald-100/70 border-emerald-900/60 bg-emerald-950/40', d: 'bg-emerald-100/50' },
  };
  const m = map[status] || map.NOT_SUBMITTED;
  return (
    <div className="inline-flex flex-col items-start gap-1">
      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${m.c}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${m.d}`} /> {(status || '').replace('_', ' ')}
      </span>
      {reason && <span className="text-[11px] text-red-300 max-w-[240px]">Reason: {reason}</span>}
    </div>
  );
};

const Table = ({ cols, rows }) => (
  <div className="mt-4 rounded-2xl border border-emerald-900/60 bg-[#06170e] overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="bg-emerald-950/50 border-b border-emerald-900/60">
            {cols.map((c) => <th key={c} className="px-4 py-3 text-[10px] font-semibold uppercase tracking-widest text-emerald-300">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-emerald-100/50">No entries.</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-emerald-900/40 last:border-0 hover:bg-emerald-950/40">
              {r.map((cell, j) => <td key={j} className="px-4 py-3 align-middle">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/* ---------------- Dividends Manager ---------------- */
const DividendsManager = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [project, setProject] = useState('ai_robot');
  const [percentage, setPercentage] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const load = async () => {
    try {
      const { data } = await API.get('/admin/dividends');
      setData(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const openPreview = async () => {
    const pct = parseFloat(percentage) || 0;
    try {
      const { data } = await API.get(`/admin/dividends/preview?percentage=${pct}`);
      const rows = (data.items || []).map((r) => ({ ...r, custom_amount: r.calculated_amount, pay: r.investment_eligible > 0 }));
      setPreviewRows(rows);
      setShowPreview(true);
    } catch (err) {
      toast({ title: 'Preview failed', description: err.response?.data?.detail || 'Please try again.' });
    }
  };

  const updateRow = (idx, field, val) => {
    const copy = [...previewRows];
    if (field === 'custom_amount') copy[idx].custom_amount = parseFloat(val || 0);
    else if (field === 'pay') copy[idx].pay = val;
    setPreviewRows(copy);
  };

  const commitSelective = async () => {
    const chosen = previewRows.filter((r) => r.pay && (r.custom_amount || 0) > 0);
    if (chosen.length === 0) return toast({ title: 'Nothing selected', description: 'Mark at least one user to pay with a non-zero amount.' });
    const total = chosen.reduce((a, r) => a + r.custom_amount, 0).toFixed(2);
    if (!window.confirm(`Pay dividend to ${chosen.length} user(s) totaling $${total}?`)) return;
    setBusy(true);
    try {
      const { data } = await API.post('/admin/dividends/distribute-selective', {
        project,
        percentage: parseFloat(percentage) || 0,
        note: note || undefined,
        items: previewRows.map((r) => ({ user_id: r.user_id, amount: r.custom_amount, pay: r.pay })),
      });
      toast({ title: 'Dividend created', description: `${data.created_count} pending payouts · ${data.skipped} skipped.` });
      setShowPreview(false); setPercentage(''); setNote('');
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally { setBusy(false); }
  };

  const distribute = async (e) => {
    e.preventDefault();
    const pct = parseFloat(percentage);
    if (isNaN(pct) || pct <= 0) return toast({ title: 'Enter a valid percentage' });
    if (!window.confirm(`Distribute ${pct}% of each investor's total investment for ${project}?`)) return;
    setBusy(true);
    try {
      const { data } = await API.post('/admin/dividends/distribute', { project, percentage: pct, note: note || undefined });
      toast({ title: 'Dividend distributed', description: `Created ${data.created_count} pending payouts for ${data.project}.` });
      setPercentage(''); setNote('');
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally { setBusy(false); }
  };

  const payDividend = async (row) => {
    const tx = window.prompt(`Enter TX hash to mark ${row.tx_id} (${row.user_email} · $${row.amount}) as PAID:`);
    if (!tx || !tx.trim()) return;
    try {
      await API.post(`/admin/dividends/${row.id}/pay`, { tx_hash: tx.trim() });
      toast({ title: 'Marked PAID', description: `${row.tx_id} · $${row.amount}` });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Please try again.' });
    }
  };

  const projects = data?.projects || [];
  const investors = data?.investors || [];
  const items = data?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-white font-serif text-2xl">Dividend Distribution</h1>
        <button onClick={load} className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 text-sm"><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <AdminStat label="Total Investment Pool" value={`$${Number(data?.grand_investment || 0).toLocaleString()}`} sub="Sum across all approved investors" />
        <AdminStat label="Total Dividend Paid" value={`$${Number(data?.grand_paid || 0).toLocaleString()}`} sub="Marked PAID lifetime" />
        <AdminStat label="Active Investors" value={String(investors.length)} sub="KYC-approved · with investments" />
      </div>

      {/* Distribute form */}
      <form onSubmit={distribute} className="rounded-2xl border border-emerald-900/60 bg-[#06170e] p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Coins size={16} className="text-emerald-400" />
          <div className="text-white font-semibold">New Distribution</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-emerald-100/60">Project</span>
            <select data-testid="dividend-project" value={project} onChange={(e) => setProject(e.target.value)} className="dark-input mt-1 w-full">
              {projects.map((p) => (
                <option key={p.id} value={p.id} disabled={p.status !== 'ACTIVE'}>
                  {p.name}{p.status !== 'ACTIVE' ? ' (Coming soon)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-emerald-100/60">Percentage (%)</span>
            <input
              data-testid="dividend-percentage"
              type="number"
              step="any"
              min="0"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              placeholder="e.g. 2.5"
              className="dark-input mt-1 w-full"
              required
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-emerald-100/60">Note (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Monthly dividend – 1st Aug 2026" className="dark-input mt-1 w-full" />
          </label>
        </div>
        <button data-testid="dividend-distribute-btn" disabled={busy} type="submit" className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-emerald-950 text-sm font-semibold px-5 py-2">
          <Plus size={14} /> {busy ? 'Creating...' : 'Distribute Dividend'}
        </button>
        <button
          type="button"
          onClick={openPreview}
          data-testid="preview-per-user-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-emerald-950 font-semibold px-5 py-2.5"
        >
          Preview per User (choose amount / skip)
        </button>
        <div className="text-[11.5px] text-emerald-100/50">Each investor receives <b>investment × percentage / 100</b> by default. Use <b>Preview per User</b> to override amounts or skip specific users.</div>
      </form>

      {/* Per-user preview + selective distribute */}
      {showPreview && (
        <div className="rounded-2xl border border-amber-500/40 bg-[#120a06] p-5" data-testid="dividend-preview-table">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="text-amber-300 font-semibold">Per-User Dividend Review</div>
              <div className="text-[11.5px] text-emerald-100/60 mt-0.5">Edit amount or untick "Pay" to skip. Only ticked users will be charged.</div>
            </div>
            <button type="button" onClick={() => setShowPreview(false)} className="text-emerald-200/70 hover:text-white text-sm underline">Close</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-emerald-100/50 border-b border-emerald-900/60">
                  <th className="text-left py-2 pr-3">Pay</th>
                  <th className="text-left py-2 pr-3">User</th>
                  <th className="text-right py-2 pr-3">Investment</th>
                  <th className="text-right py-2 pr-3">Active Shares</th>
                  <th className="text-right py-2 pr-3">Suggested</th>
                  <th className="text-right py-2 pr-3">Amount to Pay (USD)</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.length === 0 && (
                  <tr><td colSpan={6} className="text-emerald-100/40 italic py-4 text-center">No eligible users yet.</td></tr>
                )}
                {previewRows.map((r, idx) => (
                  <tr key={r.user_id} className="border-b border-emerald-900/30" data-testid={`preview-row-${r.user_id}`}>
                    <td className="py-2 pr-3">
                      <input
                        type="checkbox"
                        checked={r.pay}
                        onChange={(e) => updateRow(idx, 'pay', e.target.checked)}
                        data-testid={`preview-pay-${r.user_id}`}
                        className="h-4 w-4 accent-emerald-500"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <div className="text-white">{r.name || '—'}</div>
                      <div className="text-[10.5px] text-emerald-100/50 truncate max-w-[220px]">{r.email}</div>
                    </td>
                    <td className="text-right text-emerald-300 font-serif py-2 pr-3">${Number(r.investment_eligible).toLocaleString()}</td>
                    <td className="text-right text-emerald-100/70 py-2 pr-3">{Number(r.active_shares).toLocaleString()}</td>
                    <td className="text-right text-amber-300 py-2 pr-3">${Number(r.calculated_amount).toFixed(2)}</td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={r.custom_amount}
                        onChange={(e) => updateRow(idx, 'custom_amount', e.target.value)}
                        disabled={!r.pay}
                        data-testid={`preview-amount-${r.user_id}`}
                        className="w-28 text-right rounded-lg bg-[#0a2416] border border-emerald-800/60 text-white px-2 py-1.5 focus:outline-none focus:border-emerald-500 disabled:opacity-40"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={commitSelective}
              disabled={busy}
              data-testid="commit-selective-dividend-btn"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-emerald-950 font-semibold px-5 py-2.5"
            >
              <Save size={14} /> {busy ? 'Submitting…' : `Confirm Selected (${previewRows.filter((r) => r.pay && (r.custom_amount || 0) > 0).length})`}
            </button>
            <div className="text-[11.5px] text-emerald-100/60">
              Total: <b className="text-amber-300">${previewRows.filter((r) => r.pay).reduce((a, r) => a + (r.custom_amount || 0), 0).toFixed(2)}</b>
            </div>
          </div>
        </div>
      )}

      {/* Investor table */}
      <div className="rounded-2xl border border-emerald-900/60 bg-[#06170e] p-5">
        <div className="text-white font-semibold mb-3">Investor Totals</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-emerald-100/50 border-b border-emerald-900/60">
                <th className="text-left py-2 pr-3">Investor</th>
                <th className="text-left py-2 pr-3">BEP20 Wallet</th>
                <th className="text-right py-2 pr-3">Total Investment</th>
                <th className="text-right py-2 pr-3">Received Dividend</th>
              </tr>
            </thead>
            <tbody>
              {investors.length === 0 && <tr><td colSpan={4} className="text-emerald-100/40 italic py-4 text-center">No investors with approved packages yet.</td></tr>}
              {investors.map((i) => (
                <tr key={i.user_id} className="border-b border-emerald-900/30">
                  <td className="py-2 pr-3">
                    <div className="text-white">{i.name}</div>
                    <div className="text-[11px] text-emerald-100/50">{i.email}</div>
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-emerald-100/60 max-w-[280px] truncate">{i.bep20 || <span className="italic text-amber-300/70">not set</span>}</td>
                  <td className="py-2 pr-3 text-right text-white">${i.total_investment.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right text-emerald-300">${i.total_received.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dividend records */}
      <div className="rounded-2xl border border-emerald-900/60 bg-[#06170e] p-5">
        <div className="text-white font-semibold mb-3">Dividend Records ({items.length})</div>
        <div className="overflow-x-auto">
          <table data-testid="admin-dividend-table" className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-emerald-100/50 border-b border-emerald-900/60">
                <th className="text-left py-2 pr-3">TX ID</th>
                <th className="text-left py-2 pr-3">Date</th>
                <th className="text-left py-2 pr-3">Investor</th>
                <th className="text-left py-2 pr-3">Project</th>
                <th className="text-right py-2 pr-3">% / Amount</th>
                <th className="text-left py-2 pr-3">Status</th>
                <th className="text-left py-2 pr-3">TX Hash</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={8} className="text-emerald-100/40 italic py-4 text-center">No dividend records yet. Create one above.</td></tr>}
              {items.map((it) => (
                <tr key={it.id} className="border-b border-emerald-900/30">
                  <td className="py-2 pr-3 font-mono text-emerald-100">{it.tx_id}</td>
                  <td className="py-2 pr-3 text-emerald-100/70">{new Date(it.created_at).toLocaleDateString('en-GB')}</td>
                  <td className="py-2 pr-3 text-emerald-100/80">{it.user_email}</td>
                  <td className="py-2 pr-3 text-emerald-100/70">{it.project}</td>
                  <td className="py-2 pr-3 text-right text-white">{it.percentage}% · ${Number(it.amount).toLocaleString()}</td>
                  <td className="py-2 pr-3">
                    <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${it.status === 'PAID' ? 'bg-emerald-500 text-emerald-950' : 'bg-amber-500/20 text-amber-300'}`}>{it.status}</span>
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-emerald-100/60 truncate max-w-[180px]">{it.tx_hash || '—'}</td>
                  <td className="py-2">
                    {it.status === 'PENDING' && (
                      <button data-testid={`dividend-pay-${it.id}`} onClick={() => payDividend(it)} className="inline-flex items-center gap-1 rounded bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-[11px] font-semibold px-2.5 py-1">
                        <Check size={12} /> Mark Paid
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AdminStat = ({ label, value, sub }) => (
  <div className="rounded-2xl border border-emerald-900/60 bg-[#06170e] p-5">
    <div className="text-[10px] uppercase tracking-widest text-emerald-100/50">{label}</div>
    <div className="mt-1 font-serif text-white text-2xl">{value}</div>
    {sub && <div className="text-[11px] text-emerald-100/50 mt-1">{sub}</div>}
  </div>
);


/* ---------------- Live Chats Manager ---------------- */
const ChatsManager = () => {
  const { toast } = useToast();
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState({ session: null, messages: [] });
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await API.get('/admin/chats');
      setSessions(data.items || []);
    } catch {}
  };

  const loadThread = async (id) => {
    try {
      const { data } = await API.get(`/admin/chats/${id}`);
      setThread(data);
    } catch {}
  };

  useEffect(() => { loadSessions(); const t = setInterval(loadSessions, 8000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!selectedId) return;
    loadThread(selectedId);
    const t = setInterval(() => loadThread(selectedId), 5000);
    return () => clearInterval(t);
  }, [selectedId]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [thread]);

  const reply = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !selectedId) return;
    setBusy(true);
    try {
      await API.post(`/admin/chats/${selectedId}/reply`, { content: draft.trim() });
      setDraft('');
      loadThread(selectedId);
      loadSessions();
    } catch (err) {
      toast({ title: 'Failed to send', description: err.response?.data?.detail || 'Try again.' });
    } finally { setBusy(false); }
  };

  const closeChat = async () => {
    if (!selectedId || !window.confirm('Close this chat session?')) return;
    await API.post(`/admin/chats/${selectedId}/close`);
    loadSessions();
    loadThread(selectedId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-white font-serif text-2xl">Live Chats</h1>
        <button onClick={loadSessions} className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 text-sm"><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-4">
        {/* Sessions list */}
        <div className="rounded-2xl border border-emerald-900/60 bg-[#06170e] overflow-hidden">
          <div className="px-4 py-3 border-b border-emerald-900/60 text-emerald-100 text-sm font-semibold">Conversations ({sessions.length})</div>
          <div className="max-h-[560px] overflow-y-auto">
            {sessions.length === 0 && <div className="p-4 text-emerald-100/40 italic text-[13px]">No chat sessions yet.</div>}
            {sessions.map((s) => (
              <button
                key={s.id}
                data-testid={`chat-session-${s.id}`}
                onClick={() => setSelectedId(s.id)}
                className={`w-full text-left px-4 py-3 border-b border-emerald-900/40 transition ${selectedId === s.id ? 'bg-emerald-900/40' : 'hover:bg-emerald-900/20'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-white text-[13px] font-medium truncate">{s.first_name} {s.last_name}</div>
                  {s.unread_admin > 0 && (
                    <span className="text-[10px] font-bold bg-red-500 text-white rounded-full px-2 py-0.5">{s.unread_admin}</span>
                  )}
                </div>
                <div className="text-[11px] text-emerald-100/60 truncate">{s.email}</div>
                {s.last_message && (
                  <div className="text-[11px] text-emerald-100/40 mt-1 truncate">
                    <span className="uppercase tracking-wider">{s.last_message.role}:</span> {s.last_message.content}
                  </div>
                )}
                {s.closed && <span className="mt-1 inline-block text-[9px] font-bold uppercase tracking-widest bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded">Closed</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className="rounded-2xl border border-emerald-900/60 bg-[#06170e] flex flex-col min-h-[520px]">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-emerald-100/40 italic">Select a conversation to view</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-emerald-900/60 flex items-center justify-between">
                <div>
                  <div className="text-white text-sm font-semibold">{thread.session?.first_name} {thread.session?.last_name}</div>
                  <div className="text-[11px] text-emerald-100/50">{thread.session?.email}</div>
                </div>
                {!thread.session?.closed && (
                  <button onClick={closeChat} className="text-[11px] text-red-300 hover:text-red-100 uppercase tracking-widest">End</button>
                )}
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-black/30 max-h-[440px]">
                {thread.messages.map((m) => (
                  <AdminBubble key={m.id} m={m} />
                ))}
              </div>
              {!thread.session?.closed && (
                <form onSubmit={reply} className="p-3 border-t border-emerald-900/60 flex gap-2">
                  <input
                    data-testid="admin-chat-reply-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Type your reply as SkywayGreen Specialist…"
                    className="dark-input text-sm flex-1"
                    disabled={busy}
                  />
                  <button
                    data-testid="admin-chat-reply-send"
                    type="submit"
                    disabled={busy || !draft.trim()}
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-emerald-950 text-sm font-semibold px-4"
                  >
                    <Send size={14} /> Send
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminBubble = ({ m }) => {
  const isAdmin = m.role === 'admin';
  const isBot = m.role === 'bot';
  const align = isAdmin ? 'items-end' : 'items-start';
  const style = isAdmin
    ? 'bg-emerald-500 text-emerald-950'
    : isBot
    ? 'bg-amber-500/10 border border-amber-500/40 text-amber-100'
    : 'bg-emerald-900/40 text-emerald-50 border border-emerald-500/25';
  const label = isAdmin ? 'You (Specialist)' : isBot ? 'Auto Bot (5m timeout)' : 'Visitor';
  return (
    <div className={`flex flex-col ${align}`}>
      <div className="text-[9.5px] uppercase tracking-widest text-emerald-100/40 mb-0.5">{label}</div>
      <div className={`rounded-2xl px-3 py-2 text-[13px] leading-snug max-w-[80%] whitespace-pre-wrap ${style}`}>{m.content}</div>
      <div className="text-[9px] text-emerald-100/30 mt-0.5">
        {new Date(m.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
};


export default AdminPanel;
